/* eslint-disable no-unused-vars */
import React, { useState, useMemo } from "react";
import { Card, Radio, Tooltip, message, Table, Tag } from "antd";
import {
  BarChartOutlined,
  TableOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import { HubCPOverviewService } from "../../../services/api/hubCPOverviewService";
import ShipmentDetailsModal from "../ShipmentDetailsModal";
import "./index.scss";

// ─── Operation code mapping ────────────────────────────────────
const STATUS_TO_OPERATION_CODE = {
  INSCANNED_AT_CP: 9,
  "OUT FOR DELIVERY": 11,
  "HUB-IN-SCANNED_SHIPMENTS": 6,
  "IN-BAG-INSCAN": 40,
  INSCAN: 3,
  INSCANNED_AT_TRANSIT_SHIPMENTS: 20,
  "IN-BAG-OUTSCAN": 41,
  BAGGED: 21,
  RTO_OUT_FOR_DELIVERY: 45,
  RETURN_INITIATED: 15,
  "HUB-IN-SCANNED_BAGS": 6,
  FORCE_BAG_ATTEMPTED: 54,
  OUTSCAN_BAG: 5,
  INSCANNED_AT_TRANSIT_BAGS: 20,
  UNDELIVERED: 55,
  OUTSCAN_SHIPMENT: 5,
  OUTSCAN_TO_CP: 7,
  IN_BAG_OUTSCAN_TO_CP: 48,
};

// ─── Color + alert config ──────────────────────────────────────
const STATUS_META = {
  INSCAN:                    { color: "#1a73e8", alert: false },
  OUTSCAN:                   { color: "#34a853", alert: false },
  "IN-BAG-INSCAN":           { color: "#fbbc04", alert: false },
  UNDELIVERED:               { color: "#ea4335", alert: true  },
  RETURN:                    { color: "#ff6d6d", alert: true  },
  "OUT FOR DELIVERY":        { color: "#00acc1", alert: false },
  RETURN_INITIATED:          { color: "#ff8a65", alert: true  },
  INSCANNED_AT_TRANSIT:      { color: "#7c4dff", alert: false },
  FORCE_BAG_ATTEMPTED:       { color: "#d81b60", alert: true  },
  "HUB-IN-SCANNED":          { color: "#1e88e5", alert: false },
  "IN-BAG-OUTSCAN":          { color: "#26c6da", alert: false },
  INSCANNED_AT_CP:           { color: "#66bb6a", alert: false },
  INSCANNED:                 { color: "#9ccc65", alert: false },
  IN_BAG_OUTSCAN_TO_CP:      { color: "#ffa726", alert: false },
  RTO_OUT_FOR_DELIVERY:      { color: "#f48fb1", alert: true  },
  BAGGED:                    { color: "#90caf9", alert: false },
};

const getMeta = (status, idx) =>
  STATUS_META[status] || {
    color: `hsl(${(idx * 137.5) % 360}, 60%, 50%)`,
    alert: false,
  };

const TOP_N = 8;

// ─── Component ─────────────────────────────────────────────────
const StatusDistribution = ({
  data = [],
  selectedHubs = null,
  areAllHubsSelected = false,
  dateRange = null,
}) => {
  const [viewMode, setViewMode] = useState("chart");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [othersExpanded, setOthersExpanded] = useState(false);

  const currentData = Array.isArray(data) ? data : [];
  const grandTotal = currentData.reduce((s, i) => s + i.count, 0);

  const sortedData = useMemo(() => {
    if (!currentData.length || grandTotal === 0) return [];
    return currentData
      .filter((i) => i.count > 0)
      .map((item, idx) => {
        const meta = getMeta(item.status, idx);
        return {
          ...item,
          percentage: ((item.count / grandTotal) * 100).toFixed(1),
          color: meta.color,
          alert: meta.alert,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [currentData, grandTotal]);

  // Summary insight counts
  const alertCount = useMemo(
    () => sortedData.filter((i) => i.alert).reduce((s, i) => s + i.count, 0),
    [sortedData]
  );
  const alertPct =
    grandTotal > 0 ? ((alertCount / grandTotal) * 100).toFixed(1) : "0.0";

  const topStatuses = sortedData.slice(0, TOP_N);
  const otherStatuses = sortedData.slice(TOP_N);
  const othersTotal = otherStatuses.reduce((s, i) => s + i.count, 0);
  const othersPercentage =
    grandTotal > 0 ? ((othersTotal / grandTotal) * 100).toFixed(1) : "0.0";
  const maxCount = topStatuses[0]?.count || 1;

  // ─── Fetch detail data ────────────────────────────────────────
  const fetchClickableData = async (status, pageNum = 1, pageSize = 5) => {
    try {
      const hubIdToUse = areAllHubsSelected ? null : selectedHubs || null;
      if (!areAllHubsSelected && !hubIdToUse) {
        message.warning("Please select a hub");
        return;
      }
      setModalLoading(true);
      const fromDate =
        dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : null;
      const toDate =
        dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : null;

      const response = await HubCPOverviewService.getPieChartHubManagerOverviewClickable({
        currentHubId: hubIdToUse,
        status: status.status,
        page: pageNum - 1,
        size: pageSize,
        fromDate,
        toDate,
      });
      if (response && response.data) {
        setModalData(response.data);
        setTotalCount(response.count || 0);
        setPagination((prev) => ({
          ...prev,
          current: pageNum,
          pageSize,
          total: response.count || 0,
        }));
      }
    } catch (error) {
      message.error(error?.message || "Failed to fetch shipment details");
    } finally {
      setModalLoading(false);
    }
  };

  const handleStatusClick = async (status) => {
    setSelectedStatus(status);
    setDetailsVisible(true);
    setModalData([]);
    setPagination({ current: 1, pageSize: 5, total: 0 });
    await fetchClickableData(status, 1, 5);
  };

  const handleTableChange = (newPagination) => {
    const { current, pageSize } = newPagination;
    setPagination((prev) => ({ ...prev, current, pageSize }));
    if (selectedStatus) fetchClickableData(selectedStatus, current, pageSize);
  };

  // ─── Table columns ────────────────────────────────────────────
  const tableColumns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <div className="status-cell">
          <span
            className="status-dot"
            style={{ backgroundColor: record.color }}
          />
          <span className="status-name">{text}</span>
          {record.alert && (
            <Tag color="error" className="alert-tag">
              <WarningOutlined /> Alert
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      align: "right",
      render: (count, record) => (
        <span className="count-link" onClick={() => handleStatusClick(record)}>
          {count.toLocaleString()}
        </span>
      ),
    },
    {
      title: "%",
      dataIndex: "percentage",
      key: "percentage",
      align: "right",
      render: (p) => `${p}%`,
    },
  ];

  const displayedBars = othersExpanded ? sortedData : topStatuses;

  return (
    <>
      <Card className="status-distribution-card">
        {/* ── Header ── */}
        <div className="sd-header">
          <div className="sd-header-left">
            <h3 className="sd-title">Status Breakdown</h3>
            <div className="sd-totals">
              <span className="sd-total-chip total">
                {grandTotal.toLocaleString()} total
              </span>
              {alertCount > 0 && (
                <Tooltip title="Statuses requiring immediate attention (Undelivered, Return Initiated, Force Bag Attempted, etc.)">
                  <span className="sd-total-chip alert">
                    <WarningOutlined />
                    {alertCount.toLocaleString()} need attention ({alertPct}%)
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            size="small"
            className="view-toggle"
          >
            <Radio.Button value="chart">
              <BarChartOutlined /> Chart
            </Radio.Button>
            <Radio.Button value="table">
              <TableOutlined /> Table
            </Radio.Button>
          </Radio.Group>
        </div>

        {/* ── Empty state ── */}
        {!currentData.length || grandTotal === 0 ? (
          <div className="sd-empty">
            <p>No data available for the selected filters</p>
          </div>
        ) : viewMode === "chart" ? (
          /* ── Bar chart ── */
          <div className="sd-bar-chart">
            {displayedBars.map((item) => (
              <Tooltip
                key={item.status}
                title={`${item.status}: ${item.count.toLocaleString()} shipments (${item.percentage}%)`}
              >
                <div
                  className={`bar-row ${item.alert ? "bar-alert" : ""}`}
                  onClick={() => handleStatusClick(item)}
                >
                  <div className="bar-left">
                    <span
                      className="bar-dot"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="bar-label">{item.status}</span>
                    {item.alert && (
                      <WarningOutlined className="bar-warn-icon" />
                    )}
                  </div>

                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${(item.count / maxCount) * 100}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>

                  <div className="bar-right">
                    <span className="bar-count">
                      {item.count.toLocaleString()}
                    </span>
                    <span className="bar-pct">{item.percentage}%</span>
                  </div>
                </div>
              </Tooltip>
            ))}

            {/* Others row */}
            {otherStatuses.length > 0 && !othersExpanded && (
              <div
                className="bar-row others-row"
                onClick={() => setOthersExpanded(true)}
              >
                <div className="bar-left">
                  <span className="bar-dot" style={{ backgroundColor: "#bfbfbf" }} />
                  <span className="bar-label others-label">
                    +{otherStatuses.length} more statuses
                  </span>
                </div>
                <div className="bar-track">
                  <div
                    className="bar-fill others-fill"
                    style={{ width: `${(othersTotal / maxCount) * 100}%` }}
                  />
                </div>
                <div className="bar-right">
                  <span className="bar-count">{othersTotal.toLocaleString()}</span>
                  <span className="bar-pct">{othersPercentage}%</span>
                </div>
              </div>
            )}

            {othersExpanded && otherStatuses.length > 0 && (
              <div
                className="collapse-others"
                onClick={() => setOthersExpanded(false)}
              >
                ▲ Show less
              </div>
            )}
          </div>
        ) : (
          /* ── Table view ── */
          <div className="sd-table-view">
            <Table
              columns={tableColumns}
              dataSource={sortedData.map((item, i) => ({ ...item, key: i }))}
              pagination={false}
              size="small"
              className="status-table"
            />
          </div>
        )}
      </Card>

      <ShipmentDetailsModal
        visible={detailsVisible}
        onCancel={() => {
          setDetailsVisible(false);
          setModalData([]);
          setSelectedStatus(null);
          setPagination({ current: 1, pageSize: 5, total: 0 });
        }}
        title={
          selectedStatus ? `${selectedStatus.status} — Details` : "Shipment Details"
        }
        data={modalData}
        loading={modalLoading}
        pagination={pagination}
        onTableChange={handleTableChange}
        totalCount={totalCount}
        summaryData={null}
        operationCodes={
          selectedStatus
            ? STATUS_TO_OPERATION_CODE[selectedStatus.status]
            : null
        }
        hubId={areAllHubsSelected ? null : selectedHubs || null}
        isPieChart={true}
        status={selectedStatus?.status}
        dateRange={dateRange}
      />
    </>
  );
};

StatusDistribution.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({ status: PropTypes.string, count: PropTypes.number })
  ),
  selectedHubs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  areAllHubsSelected: PropTypes.bool,
  dateRange: PropTypes.array,
};

export default StatusDistribution;
